import { Activity, ActivityType } from '../activity/activity.class';
import { addMinutes } from 'date-fns';

/** Medical inquiry statuscode option set values (indskr_medicalinquiry.statuscode) */
export enum MedicalInquiryStatus {
  DRAFT = 1,
  SUBMITTED = 268890001,
  INACTIVE = 2,
  COMPLETED = 268890002,
}

const STATUS_CODE_LABELS: Record<number, string> = {
  [MedicalInquiryStatus.DRAFT]: 'Draft',
  [MedicalInquiryStatus.SUBMITTED]: 'Submitted',
  [MedicalInquiryStatus.INACTIVE]: 'Inactive',
  [MedicalInquiryStatus.COMPLETED]: 'Completed',
};

export const MedicalInquiryFetchAttributes = [
  'indskr_medicalinquiryid',
  'indskr_title',
  'createdon',
  'indskr_repsignature',
  'statuscode',
  'statecode',
  'indskr_ndc',
  'indskr_reporteddate',
  'indskr_reporttype',
  'overriddencreatedon',
  'indskr_primaryreporter',
  'indskr_packagesize',
  'owningbusinessunit',
  'ownerid',
  'modifiedon',
  'modifiedonbehalfby',
  'modifiedby',
  'indskr_lotnumber',
  'indskr_hcpsignature',
  'indskr_hcpsignaturedate',
  'indskr_usersignaturedate',
  'indskr_hcpsignatureterms',
  'indskr_expirydate',
  'indskr_strength',
  'indskr_dosageform',
  'indskr_description',
  'createdonbehalfby',
  'createdby',
  'indskr_contact',
  'indskr_consumerrelated',
  'indskr_casetype',
  'indskr_availableforreturn',
  'indskr_autogeneratetitle',
  'indskr_address',
  'indskr_id',
  'indskr_appointment',
  'indskr_preferredmodeofcontact',
  'indskr_phonenumber',
  'indskr_emailaddress',
  'indskr_faxnumber',
] as const;

/** Raw keys needed for MedicalInquiry (constructor + display form). Single source of truth. */
const RAW_KEYS_NEEDED = new Set<string>([
  ...MedicalInquiryFetchAttributes,
  // omp_question is org-specific (not in the core fetch list, see dynamic form-driven
  // attribute resolution in medical-inquiry.data.service.ts) but must still survive
  // pickNeededFields for the idempotency-check upsert path, which reads from an
  // <all-attributes/> fetch regardless of org config.
  'omp_question',
  'id',
  '_indskr_contact_value',
  '_indskr_contact_value@OData.Community.Display.V1.FormattedValue',
  '_ownerid_value',
  '_ownerid_value@OData.Community.Display.V1.FormattedValue',
  '_indskr_casetype_value',
  '_indskr_casetype_value@OData.Community.Display.V1.FormattedValue',
  '_indskr_reporttype_value',
  '_indskr_reporttype_value@OData.Community.Display.V1.FormattedValue',
  '_indskr_address_value',
  '_indskr_address_value@OData.Community.Display.V1.FormattedValue',
  '_indskr_appointment_value',
  '_indskr_appointment_value@OData.Community.Display.V1.FormattedValue',
  'statuscode@OData.Community.Display.V1.FormattedValue',
  'statecode@OData.Community.Display.V1.FormattedValue',
]);

export class MedicalInquiry extends Activity {
  id: string;
  title: string;
  stateCode: number;
  statusCode: number;
  statusCodeFormatted: string;
  reportedDate: Date;
  reportType: number;
  reportTypeFormatted: string;
  caseType: number;
  caseTypeFormatted: string;
  contactId: string;
  contactFormatted: string;
  createdOn: Date;
  modifiedOn: Date;
  rawData: Record<string, unknown>;
  /** ID of the linked appointment (meeting). Populated from Dynamics field indskr_appointment (_indskr_appointment_value). */
  appointmentId: string;
  /** Products linked to this MI. Populated post-construction by the data service. Never stored in rawData. */
  products: Array<{ id: string; name: string }> = [];

  /** Keep only raw keys needed by the model. Use before constructing from API response. */
  static pickNeededFields(raw: Record<string, unknown>): Record<string, unknown> {
    if (!raw || typeof raw !== 'object') return {};
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(raw)) {
      if (k.startsWith('@odata.')) continue;
      if (RAW_KEYS_NEEDED.has(k) || k.endsWith('@OData.Community.Display.V1.FormattedValue') || (k.startsWith('_') && k.endsWith('_value'))) {
        out[k] = raw[k];
      }
    }
    return out;
  }

  /** Build model from API response, keeping only needed fields. */
  static fromApiResponse(raw: Record<string, unknown>): MedicalInquiry {
    return new MedicalInquiry(MedicalInquiry.pickNeededFields(raw));
  }

  constructor(raw: Record<string, unknown> = {}) {
    // Prepare raw data for the Activity base class
    const reportedDateVal = raw['indskr_reporteddate'] || raw['createdon'] || new Date().toISOString();
    const startDate = new Date(reportedDateVal as string | number);
    const endDate = new Date(startDate.getTime() + 30 * 60 * 1000);
    const activityRaw = {
      activitytypecode: 'indskr_medicalinquiry',
      activityid: raw['indskr_medicalinquiryid'] || raw['id'],
      subject: raw['indskr_title'] || '',
      scheduledstart: startDate.toISOString(),
      scheduledend: endDate.toISOString(),
      statecode: raw['statecode'] ?? 0,
      statuscode: raw['statuscode'] ?? 0,
      createdon: raw['createdon'],
      indskr_ownerid: raw['_ownerid_value'] || raw['ownerid'],
      indskr_ownerfullname: raw['_ownerid_value@OData.Community.Display.V1.FormattedValue'] || '',
      ...raw,
    };
    super(activityRaw);

    // Override type explicitly (base class switch may also set it, but be safe)
    this.type = ActivityType.MedicalInquiry;

    // MI-specific field mapping
    this.id = (raw['indskr_medicalinquiryid'] as string) || (raw['id'] as string) || '';
    this.ID = this.id;
    this.title = (raw['indskr_title'] as string) || '';
    this.stateCode = raw['statecode'] != null ? Number(raw['statecode']) : 0;
    this.statusCode = raw['statuscode'] != null ? Number(raw['statuscode']) : 0;
    this.statusCodeFormatted =
      (raw['statuscode_Formatted'] as string) ||
      (raw['_statuscode_value@OData.Community.Display.V1.FormattedValue'] as string) ||
      STATUS_CODE_LABELS[this.statusCode] ||
      '';
    this.reportedDate = raw['indskr_reporteddate']
      ? new Date(raw['indskr_reporteddate'] as string | number)
      : null;
    this.reportType = raw['indskr_reporttype'] != null ? Number(raw['indskr_reporttype']) : null;
    this.reportTypeFormatted =
      (raw['indskr_reporttype_Formatted'] as string) ||
      (raw['_indskr_reporttype_value@OData.Community.Display.V1.FormattedValue'] as string) ||
      '';
    this.caseType = raw['indskr_casetype'] != null ? Number(raw['indskr_casetype']) : null;
    this.caseTypeFormatted =
      (raw['indskr_casetype_Formatted'] as string) ||
      (raw['_indskr_casetype_value@OData.Community.Display.V1.FormattedValue'] as string) ||
      '';
    this.contactId = (raw['_indskr_contact_value'] as string) || (raw['indskr_contact'] as string) || '';
    this.contactFormatted =
      (raw['_indskr_contact_value@OData.Community.Display.V1.FormattedValue'] as string) || '';
    const createdOnRaw = raw['createdon'] ?? raw['offlineCreatedAt'];
    this.createdOn = createdOnRaw != null ? new Date(createdOnRaw as string | number) : null;
    this.modifiedOn = raw['modifiedon'] ? new Date(raw['modifiedon'] as string | number) : null;
    this.ownerId = (raw['_ownerid_value'] as string) || (raw['ownerid'] as string) || '';
    this.appointmentId = (raw['_indskr_appointment_value'] as string) || (raw['indskr_appointment'] as string) || '';
    this.rawData = { ...raw };

    // Ensure scheduledStart/End are valid for Agenda
    this.scheduledStart = startDate;
    this.scheduledEnd = endDate;
    this.scheduledStartLocale = this.scheduledStart.toLocaleTimeString('en-US', { hour12: true, hour: '2-digit', minute: '2-digit' });
  }

  get isActive(): boolean {
    return this.stateCode === 0;
  }

  get isDraft(): boolean {
    return this.statusCode === MedicalInquiryStatus.DRAFT;
  }

  get isSubmitted(): boolean {
    return this.statusCode === MedicalInquiryStatus.SUBMITTED;
  }

  get isCompleted(): boolean {
    return this.statusCode === MedicalInquiryStatus.COMPLETED;
  }

  get isInactive(): boolean {
    return this.statusCode === MedicalInquiryStatus.INACTIVE || !this.isActive;
  }

  /** Returns the inquiry title for display. Never uses id as fallback—callers should use "Medical Inquiry" when empty. */
  get displayTitle(): string {
    return this.title || '';
  }
}
